import React, { useState, useCallback, useEffect } from 'react'
import {
  Box,
  Typography,
  Checkbox,
  Collapse,
  IconButton,
  Divider,
  CircularProgress,
  TextField,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import {
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  SlidersHorizontal,
  FileText,
  Building,
  MapPin,
  Briefcase,
  Activity,
  Calendar,
  DollarSign,
} from 'lucide-react'
import {
  setEntreprisesFilters,
  clearEntreprisesFilters,
  searchLegalNotices,
} from '../../../redux/actions/entreprisesAction'
import { useSearchTokens } from '../../SearchScreen/searchDesignTokens'

const SIDEBAR_WIDTH = 280

// ── Count Badge ─────────────────────────────────────────────────────────

const CountBadge = ({ count, dk }) => (
  <Typography
    component="span"
    sx={{
      fontSize: '0.68rem',
      fontWeight: 600,
      color: dk.text3,
      backgroundColor: dk.chipBg,
      borderRadius: '10px',
      px: 0.8,
      py: '1px',
      ml: 'auto',
      minWidth: 20,
      textAlign: 'center',
      lineHeight: 1.6,
    }}
  >
    {count}
  </Typography>
)

// ── Filter Item ─────────────────────────────────────────────────────────

const FilterItem = ({ label, count, checked, onChange, dk }) => (
  <Box
    onClick={onChange}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      py: 0.4,
      px: 1,
      cursor: 'pointer',
      borderRadius: '6px',
      transition: 'background-color 0.15s',
      '&:hover': { backgroundColor: dk.hover },
    }}
  >
    <Checkbox
      checked={checked}
      size="small"
      sx={{
        p: 0.3,
        color: dk.disabled,
        '&.Mui-checked': { color: dk.gold },
        '& .MuiSvgIcon-root': { fontSize: '1.1rem' },
      }}
    />
    <Typography
      sx={{
        fontSize: '0.78rem',
        fontWeight: checked ? 600 : 400,
        color: checked ? dk.text1 : dk.text2,
        flexGrow: 1,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {label}
    </Typography>
    {count > 0 && <CountBadge count={count} dk={dk} />}
  </Box>
)

// ── Section Header ──────────────────────────────────────────────────────

const SectionHeader = ({ icon, title, expanded, onToggle, activeCount, dk }) => (
  <Box
    onClick={onToggle}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      py: 1,
      px: 1,
      cursor: 'pointer',
      borderRadius: '8px',
      userSelect: 'none',
      transition: 'background-color 0.15s',
      '&:hover': { backgroundColor: dk.hover },
    }}
  >
    <Box sx={{ color: activeCount > 0 ? dk.gold : dk.text2, display: 'flex' }}>
      {icon}
    </Box>
    <Typography
      sx={{
        fontSize: '0.76rem',
        fontWeight: 700,
        color: dk.text1,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        flexGrow: 1,
      }}
    >
      {title}
    </Typography>
    {activeCount > 0 && (
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          backgroundColor: dk.gold,
          color: '#000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.62rem',
          fontWeight: 700,
        }}
      >
        {activeCount}
      </Box>
    )}
    {expanded ? (
      <ChevronDown size={14} color={dk.disabled} />
    ) : (
      <ChevronRight size={14} color={dk.disabled} />
    )}
  </Box>
)

// ── Main Component ──────────────────────────────────────────────────────

const EntreprisesFilterPanel = ({ mobile = false, onClose }) => {
  const dispatch = useDispatch()
  const dk = useSearchTokens()
  const { t } = useTranslation()

  const facets = useSelector((state) => state.entreprises.facets)
  const filters = useSelector((state) => state.entreprises.filters)
  const isSearching = useSelector((state) => state.entreprises.loading.search)

  const [isOpen, setIsOpen] = useState(true)
  const [expandedSections, setExpandedSections] = useState({
    type_annonce: true,
    forme_juridique: true,
    ville: false,
    secteur_principal: false,
    statut_societe: true,
    annee_publication: false,
    capital: false,
  })

  // Stable facets to avoid flicker
  const [stableFacets, setStableFacets] = useState(facets)
  useEffect(() => {
    const hasData = Object.values(facets).some(
      (arr) => Array.isArray(arr) && arr.length > 0
    )
    if (hasData) setStableFacets(facets)
  }, [facets])

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const handleFilterToggle = useCallback(
    (filterKey, value) => {
      const currentValues = filters[filterKey] || []
      const newValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value]

      const newFilters = { ...filters }
      if (newValues.length > 0) {
        newFilters[filterKey] = newValues
      } else {
        delete newFilters[filterKey]
      }

      dispatch(setEntreprisesFilters(newFilters))
      dispatch(searchLegalNotices({ filters: newFilters, page: 1 }))
    },
    [filters, dispatch]
  )

  const isChecked = (filterKey, value) => {
    return (filters[filterKey] || []).includes(value)
  }

  const getActiveCount = (filterKey) => {
    return (filters[filterKey] || []).length
  }

  const handleClearAll = () => {
    dispatch(clearEntreprisesFilters())
    dispatch(searchLegalNotices({ filters: {}, page: 1 }))
  }

  const hasAnyFacets = Object.values(stableFacets).some(
    (arr) => Array.isArray(arr) && arr.length > 0
  )

  const totalActiveFilters = Object.keys(filters).reduce((acc, key) => {
    const val = filters[key]
    if (Array.isArray(val)) return acc + val.length
    if (val !== null && val !== undefined && val !== '') return acc + 1
    return acc
  }, 0)

  // Collapsed strip
  if (!mobile && !isOpen) {
    return (
      <Box
        sx={{
          width: 40,
          minHeight: '100%',
          borderRight: `1px solid ${dk.border}`,
          backgroundColor: dk.surface,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 1.5,
        }}
      >
        <IconButton
          size="small"
          onClick={() => setIsOpen(true)}
          sx={{
            color: dk.text2,
            '&:hover': { backgroundColor: dk.goldSub },
          }}
        >
          <SlidersHorizontal size={16} />
        </IconButton>
      </Box>
    )
  }

  const FILTER_SECTIONS = [
    {
      key: 'type_annonce',
      icon: <FileText size={14} />,
      title: t('type_annonce', "Type d'annonce"),
      translateLabel: (v) => t(`type_${v}`, v),
    },
    {
      key: 'forme_juridique',
      icon: <Building size={14} />,
      title: t('forme_juridique', 'Forme juridique'),
      translateLabel: (v) => t(`forme_${v}`, v),
    },
    {
      key: 'ville',
      icon: <MapPin size={14} />,
      title: t('ville', 'Ville'),
      translateLabel: (v) => v,
    },
    {
      key: 'secteur_principal',
      icon: <Briefcase size={14} />,
      title: t('secteur_principal', 'Secteur'),
      translateLabel: (v) => t(`secteur_${v}`, v),
    },
    {
      key: 'statut_societe',
      icon: <Activity size={14} />,
      title: t('statut_societe', 'Statut'),
      translateLabel: (v) => t(`statut_${v}`, v),
    },
    {
      key: 'annee_publication',
      icon: <Calendar size={14} />,
      title: t('annee_publication', 'Année'),
      translateLabel: (v) => String(v),
    },
  ]

  return (
    <Box
      data-tour="entreprises-filters"
      sx={{
        width: mobile ? '100%' : SIDEBAR_WIDTH,
        minWidth: mobile ? 'auto' : SIDEBAR_WIDTH,
        minHeight: '100%',
        borderRight: mobile ? 'none' : `1px solid ${dk.border}`,
        backgroundColor: dk.surface,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 1.2,
          borderBottom: `1px solid ${dk.border}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <SlidersHorizontal size={14} color={dk.text2} />
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: dk.text1 }}>
            {t('filters')}
          </Typography>
          {isSearching && (
            <CircularProgress size={10} thickness={5} sx={{ color: dk.gold, ml: 0.5 }} />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {totalActiveFilters > 0 ? (
            <Typography
              onClick={handleClearAll}
              sx={{
                fontSize: '0.68rem',
                color: dk.gold,
                cursor: 'pointer',
                fontWeight: 500,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {t('clear_all_filters')}
            </Typography>
          ) : hasAnyFacets && (
            <Typography
              onClick={() => dispatch(searchLegalNotices({ q: '', filters: {}, page: 1 }))}
              sx={{
                fontSize: '0.68rem',
                color: dk.text3,
                cursor: 'pointer',
                fontWeight: 500,
                transition: 'color 0.15s',
                '&:hover': { color: dk.gold, textDecoration: 'underline' },
              }}
            >
              {t('show_all', 'Voir tout')}
            </Typography>
          )}
          {!mobile && (
            <IconButton
              size="small"
              onClick={() => setIsOpen(false)}
              sx={{ color: dk.disabled, p: 0.3, '&:hover': { backgroundColor: dk.goldSub } }}
            >
              <ChevronLeft size={14} />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Scrollable filters */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          px: 0.5,
          py: 1,
          opacity: isSearching ? 0.5 : 1,
          transition: 'opacity 0.2s',
          pointerEvents: isSearching ? 'none' : 'auto',
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: dk.scrollThumb,
            borderRadius: 2,
          },
        }}
      >
        {!hasAnyFacets && (
          <Typography
            variant="body2"
            sx={{ color: dk.disabled, textAlign: 'center', mt: 4, px: 2 }}
          >
            {t('search_to_see_filters', 'Recherchez pour voir les filtres')}
          </Typography>
        )}

        {FILTER_SECTIONS.map(({ key, icon, title, translateLabel }) => {
          let items = stableFacets[key]
          if (!Array.isArray(items) || items.length === 0) return null
          if (key === 'annee_publication') {
            items = [...items].sort((a, b) => Number(b.value) - Number(a.value))
          }

          return (
            <React.Fragment key={key}>
              <SectionHeader
                icon={icon}
                title={title}
                expanded={expandedSections[key]}
                onToggle={() => toggleSection(key)}
                activeCount={getActiveCount(key)}
                dk={dk}
              />
              <Collapse in={expandedSections[key]}>
                <Box sx={{ pb: 1 }}>
                  {items.map(({ value, count }) => (
                    <FilterItem
                      key={value}
                      label={translateLabel(value)}
                      count={count}
                      checked={isChecked(key, value)}
                      onChange={() => handleFilterToggle(key, value)}
                      dk={dk}
                    />
                  ))}
                </Box>
              </Collapse>
              <Divider sx={{ mx: 1, my: 0.5, borderColor: dk.border }} />
            </React.Fragment>
          )
        })}
      </Box>
    </Box>
  )
}

export default EntreprisesFilterPanel
